// ============================================================
// BmsGroupContent.jsx  — v2  (B-1 ~ B-6 전면 개선)
// ============================================================
import React from 'react';
import { PI, PF, ModalSectionTitle } from '../shared/helpers.jsx';
import { computeBatteryLosses, R_int_T } from '../../core/physics/battery.js'; // [P7-4] Ri 손실 KPI
import { mono } from '../shared/theme.js';
// [D4-Ri] Ri@T 스위프 차트용 recharts
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

export function BmsGroupContent({ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e,
  getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault,
  selLutMode, setSelLutMode, selAwdMode, setSelAwdMode,
  selRegenMode, setSelRegenMode, regenView, setRegenView,
  piEditMode, setPiEditMode,
  hideY2_ctrl_kw, set_hideY2_ctrl_kw,
}) {
  const Tog = ({ k, onColor='#4ade80', size=10 }) => (
    <button onClick={()=>up(k,!(params[k]??false))}
      style={{ padding:'2px 12px', borderRadius:'4px', border:'none', cursor:'pointer',
        fontSize:size, fontWeight:700,
        background:(params[k]??false)?onColor:COLORS.border,
        color:(params[k]??false)?'#000':COLORS.textMuted }}>
      {(params[k]??false)?'ON':'OFF'}
    </button>
  );

  const KpiRow = ({ items }) => (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${items.length},1fr)`, gap:'6px', marginBottom:'10px' }}>
      {items.map((r,i)=>(
        <div key={i} style={{ background:COLORS.bg, borderRadius:'6px', padding:'7px', textAlign:'center', border:`1px solid ${(r.c||COLORS.border)}30` }}>
          <div style={{ fontSize:7.5, color:COLORS.textMuted, fontFamily:mono }}>{r.l}</div>
          <div style={{ fontSize:'13px', fontWeight:800, color:r.c||COLORS.text, fontFamily:mono }}>{r.v}<span style={{fontSize:'8px',color:COLORS.textDim}}> {r.u||''}</span></div>
        </div>
      ))}
    </div>
  );

  const TogglesRow = ({ items }) => (
    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'12px',
      padding:'8px 12px', borderRadius:'7px', background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
      <span style={{fontSize:9.5,fontWeight:700,color:COLORS.textMuted,alignSelf:'center'}}>
        {e('기능 토글','Feature Toggles')}:
      </span>
      {items.map(t=>(
        <div key={t.k} style={{display:'flex',alignItems:'center',gap:'4px'}}>
          <span style={{fontSize:8.5,color:COLORS.textMuted}}>{t.label}</span>
          <Tog k={t.k} onColor={t.c||'#4ade80'}/>
          {t.warn && !(params[t.k]??false) && <span style={{fontSize:7.5,color:'#f87171'}}>⚠</span>}
        </div>
      ))}
    </div>
  );

  return (
    <div>

    {/* ══════════════════════════════════════════════════════════
        BMS 제어 구조 블럭도 — Cascade PID 스타일 5열 구조
        열1: 셀/데이터  열2: 상태추정  열3: 보호/제어  열4: 출력  열5: 배터리팩
    ══════════════════════════════════════════════════════════ */}
    {group === 'bms' && (() => {
      // ── 색상 팔레트 ──────────────────────────────────────────
      const C_CELL = '#818cf8';   // 셀·데이터 수집
      const C_EST  = '#f59e0b';   // 상태 추정 (SOC·SoH·칼만)
      const C_PROT = '#ef4444';   // 보호·제어 로직
      const C_OUT  = '#f97316';   // 출력 제어 (릴레이·밸런싱)
      const C_PACK = '#22c55e';   // 배터리팩 (결과)
      const C_MOD  = '#a78bfa';   // 모델 지원 (2RC·SEI·열)
      const C_ARB  = '#06b6d4';   // 중재 (Arbitration)
      const C_NEU  = '#64748b';   // 중립 (외부 신호)

      // ── 현재 활성 탭 판정 ────────────────────────────────────
      const isCell = ['bms_cell'].includes(subTab);
      const isEst  = ['bms_soc','kalman_ext'].includes(subTab);
      const isProt = ['bms_volt'].includes(subTab);
      const isOut  = ['bms_output'].includes(subTab);

      // ── 실시간 KPI 값 ────────────────────────────────────────
      const S       = params.bms_cell_count_s || 96;
      const P       = params.bms_cell_count_p || 4;
      const soc_pct = ((params.soc_init || 0.9) * 100).toFixed(0) + '%';
      const V_nom   = (3.7 * S).toFixed(0) + 'V';
      const kf_type = params.kalman_enable
        ? (params.kalman_type || 'EKF')
        : 'OFF';
      const bat_mdl = params.battery_model === '2RC' ? '2RC ECM' : 'Simple R';
      const i_lim   = `${params.bms_i_charge_max || 200}/${params.bms_i_discharge_max || 500}A`;
      const arb_kw  = (params.arb_chg_limit_kw || 70) + 'kW';

      // ── 열 컨테이너 (MotorGroupContent의 col과 동일 패턴) ─────
      const col = (name, bw, color, active, children) => (
        <div style={{ display:'flex', flexDirection:'column', gap:0,
          flex:1, minWidth:0, borderRadius:'8px', overflow:'hidden',
          border:`2px solid ${active ? color : color + '35'}`,
          background: active ? `${color}0c` : `${color}05`,
          transition:'all 0.2s' }}>
          <div style={{ padding:'6px 8px',
            background: active ? `${color}22` : `${color}12`,
            borderBottom:`1px solid ${color}30`,
            display:'flex', flexDirection:'column',
            alignItems:'center', gap:'2px' }}>
            <span style={{ fontSize:'9px', fontWeight:800, color, textAlign:'center', lineHeight:1.2 }}>{name}</span>
            <span style={{ fontSize:'7px', color:COLORS.textMuted, fontFamily:mono, textAlign:'center', lineHeight:1.3 }}>{bw}</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px', padding:'6px', flex:1 }}>
            {children}
          </div>
        </div>
      );

      // ── 셀 블럭 ──────────────────────────────────────────────
      const cell = (icon, title, sub, color, tabs) => {
        const active = tabs && tabs.includes(subTab);
        return (
          <div onClick={() => tabs && tabs.length > 0 && setSubTab(tabs[0])}
            style={{ display:'flex', alignItems:'center', gap:'6px',
              padding:'5px 7px', borderRadius:'6px',
              background: active ? `${color}30` : `${color}14`,
              border:`1.5px solid ${active ? color : color + '50'}`,
              boxShadow: active ? `0 0 7px ${color}50` : 'none',
              cursor: tabs && tabs.length > 0 ? 'pointer' : 'default',
              transition:'all 0.15s', width:'100%' }}>
            <span style={{ fontSize:'13px', lineHeight:1, flexShrink:0 }}>{icon}</span>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:8.5, fontWeight:700, color: active ? color : COLORS.text, lineHeight:1.2 }}>{title}</div>
              {sub && <div style={{ fontSize:'7px', color:COLORS.textMuted, fontFamily:mono, lineHeight:1.3, marginTop:'1px' }}>{sub}</div>}
            </div>
          </div>
        );
      };

      // ── 수평 화살표 ──────────────────────────────────────────
      const hArr = (color = COLORS.textMuted, label = '') => (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
          justifyContent:'center', flexShrink:0, padding:'0 1px', alignSelf:'center', gap:'1px' }}>
          <span style={{ fontSize:'14px', color, lineHeight:1 }}>→</span>
          {label && <span style={{ fontSize:'6px', color, textAlign:'center', whiteSpace:'nowrap' }}>{label}</span>}
        </div>
      );

      // ── BW/구분 계산 ─────────────────────────────────────────
      const soc_ok    = params.kalman_enable && params.battery_model === '2RC';
      const bal_ok    = params.bms_detailed_enable;
      const sei_ok    = params.sei_enable;
      const soh_ok    = params.soh_enable;

      return (
        <div style={{ marginBottom:'12px', padding:'10px 12px', borderRadius:'8px',
          background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>

          {/* ── 헤더 ── */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'9px' }}>
            <span style={{ fontSize:'10px', fontWeight:700, color:COLORS.accent3 }}>
              🔋 {e('BMS 제어 구조 — 전력·상태·보호 통합 흐름','BMS Control Architecture — Power · State · Protection')}
            </span>
            <span style={{ fontSize:7.5, color:COLORS.textMuted }}>
              {e('클릭 → 탭 이동','Click → tab')}
            </span>
            {/* 핵심 상태 배지 */}
            <div style={{ marginLeft:'auto', display:'flex', gap:'5px' }}>
              {[
                { label: `2RC: ${bat_mdl==='2RC ECM'?'ON':'OFF'}`, ok: bat_mdl==='2RC ECM', c: C_MOD },
                { label: `KF: ${kf_type}`, ok: params.kalman_enable, c: C_EST },
                { label: `SEI: ${sei_ok?'ON':'OFF'}`, ok: sei_ok, c:'#f59e0b' },
                { label: `SoH: ${soh_ok?'ON':'OFF'}`, ok: soh_ok, c:'#34d399' },
              ].map((b,i) => (
                <div key={i} style={{ padding:'2px 6px', borderRadius:'4px', fontSize:7.5, fontFamily:mono,
                  background: b.ok ? `${b.c}18` : `${C_NEU}12`,
                  border:`1px solid ${b.ok ? b.c+'50' : C_NEU+'30'}`,
                  color: b.ok ? b.c : COLORS.textMuted }}>
                  {b.ok ? '✓' : '○'} {b.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── 5열 수평 블럭도 ── */}
          <div style={{ display:'flex', alignItems:'stretch', gap:'2px' }}>

            {/* 열1: 셀·데이터 수집 */}
            {col(
              e('셀·데이터','Cell·Data'),
              `${S}S${P}P · ${(S*P)}셀`,
              C_CELL, isCell,
              <>
                {cell('📡', e('전압 측정','V Measure'),  `${S}셀 × ${(params.bms_v_cell_max||4.2)}V`,                      C_NEU,  [])}
                {cell('🌡️', e('온도 측정','T Measure'),  `${params.T_amb||25}°C 기준`,                                      C_NEU,  [])}
                {cell('⚡', e('전류 측정','I Measure'),  `±${params.bms_i_discharge_max||500}A`,                            C_NEU,  [])}
                {cell('🔢', e('데이터 수집','Data Acq.'), `A/D·MUX·필터`,                                                   C_CELL, ['bms_cell'])}
                {cell('🔋', e('배터리 모델','Batt Model'), bat_mdl,                                                         C_MOD,  ['bms_cell'])}
              </>
            )}

            {hArr(C_EST, e('원시값','Raw'))}

            {/* 열2: 상태 추정 */}
            {col(
              e('상태 추정','State Est.'),
              `SOC=${soc_pct} · KF=${kf_type}`,
              C_EST, isEst,
              <>
                {cell('🧠', 'SOC 추정',   `컬롱 카운팅 → ${kf_type}`,                                                      C_EST,  ['bms_soc'])}
                {cell('📡', e('칼만 필터','Kalman Filter'), `${kf_type} · Q=${(params.Q_kf||1e-5).toExponential(0)}`,     C_EST,  ['kalman_ext'])}
                {cell('📈', 'OCV 모델',   params.ocv_lut_enable ? 'LUT+Nernst' : 'Nernst only',                          C_MOD,  ['bms_cell'])}
                {cell('🧬', 'SEI / SoH',   [sei_ok?'SEI':'',soh_ok?'SoH':''].filter(Boolean).join('·')||'비활성',        sei_ok||soh_ok?'#f59e0b':C_NEU, ['bms_cell'])}
                {cell('⚠️', 'Li Plating',  params.li_plating_enable ? 'ON' : 'OFF',                                       params.li_plating_enable?'#f87171':C_NEU, ['bms_cell'])}
              </>
            )}

            {hArr(C_PROT, e('상태','State'))}

            {/* 열3: 보호·제어 */}
            {col(
              e('보호·제어','Protection'),
              `V: ${params.bms_v_cell_min||2.8}~${params.bms_v_cell_max||4.2}V`,
              C_PROT, isProt,
              <>
                {cell('🛡️', e('전압 보호','V Protection'),   `OVP/UVP·CC/CV`,                                             C_PROT, ['bms_volt'])}
                {cell('🌡️', e('온도 보호','T Protection'),   `${params.bms_t_discharge_min||-20}~${params.bms_t_discharge_max||60}°C`, C_PROT, ['bms_soc'])}
                {cell('📊', 'SOC 윈도우',   `${(params.bms_soc_discharge_cutoff||0.05)*100|0}~${(params.bms_soc_charge_cutoff||0.98)*100|0}%`, C_PROT, ['bms_soc'])}
                {cell('⚖️', e('중재 (Arb)','Arbitration'),   `chg≤${arb_kw}`,                                             C_ARB,  ['bms_soc'])}
                {cell('🔥', e('열 모델','Thermal Model'),    `C=${((params.C_th_batt||80000)/1000).toFixed(0)}kJ/°C`,     C_MOD,  ['bms_soc'])}
              </>
            )}

            {hArr(C_OUT, e('명령','Cmd'))}

            {/* 열4: 출력 제어 */}
            {col(
              e('출력 제어','Output Ctrl'),
              i_lim,
              C_OUT, isOut,
              <>
                {cell('🔌', e('릴레이·콘택터','Relay·Contactor'), `프리차지 ${params.bms_precharge_v||350}V`,              C_OUT,  ['bms_output'])}
                {cell('⚡', e('전류 제한','Current Limit'),        `chg ${params.bms_i_charge_max||200}A`,                 C_OUT,  ['bms_output'])}
                {cell('⚡', e('전류 제한','Discharge Limit'),      `dis ${params.bms_i_discharge_max||500}A`,              C_OUT,  ['bms_output'])}
                {cell('⚖️', e('밸런싱','Balancing'),               `${params.bms_balance_strategy||'passive'} ΔV>${((params.bms_balance_threshold||0.01)*1000).toFixed(0)}mV`, bal_ok?C_OUT:C_NEU, ['bms_output'])}
                {cell('🛡️', e('결함 래치','Fault Latch'),           params.bms_fault_latch ? 'ON' : 'OFF',                 params.bms_fault_latch?'#ef4444':C_NEU, ['bms_output'])}
              </>
            )}

            {hArr(C_PACK, e('HV 출력','HV Out'))}

            {/* 열5: 배터리팩 결과 */}
            {col(
              e('배터리 팩','Battery Pack'),
              `${V_nom} · ${params.battery_energy||75}kWh`,
              C_PACK, false,
              <>
                {cell('🔋', e('고전압 버스','HV Bus'),      `${V_nom} (nom)`,                                               C_PACK, [])}
                {cell('⚡', e('인버터 연결','→ Inverter'),   `V_dc ${params.V_dc||400}V`,                                   C_PACK, [])}
                {cell('🔥', e('열관리','Thermal Mgmt'),     `T_batt=${params.T_amb||25}°C`,                                C_MOD,  [])}
                {cell('📊', e('SOC 출력','SOC Output'),     soc_pct,                                                        C_EST,  [])}
                {cell('🛡️', e('안전 차단','Safety Cutoff'), params.bms_fault_latch?'래치 활성':'수동',                      params.bms_fault_latch?'#ef4444':C_NEU, [])}
              </>
            )}

          </div>

          {/* ── 파라미터 요약 배지 행 ── */}
          <div style={{ marginTop:'8px', padding:'6px 10px', borderRadius:'6px',
            background:`${C_MOD}08`, border:`1px solid ${C_MOD}25`,
            display:'flex', flexWrap:'wrap', gap:'2px 10px', fontSize:7.5, fontFamily:mono }}>
            <span style={{ color:C_MOD, fontWeight:700, flexShrink:0 }}>
              ⚙️ {e('배터리 모델 파라미터','Battery Model Params')}
            </span>
            {[
              { k:'Model',   v: bat_mdl,                                                    c: C_MOD },
              { k:'R0',      v: `${((params.R0_base||params.R_batt||0.08)*1000).toFixed(1)}mΩ`, c: C_MOD },
              { k:'τ1',      v: `${((params.R1_base||0.02)*(params.C1_base||1500)).toFixed(0)}s`, c: C_MOD },
              { k:'τ2',      v: `${((params.R2_base||0.01)*(params.C2_base||10000)).toFixed(0)}s`, c: C_MOD },
              { k:'arb_chg', v: arb_kw,                                                    c: C_ARB },
              { k:'C_th',    v: `${((params.C_th_batt||80000)/1000).toFixed(0)}kJ/°C`,     c:'#f97316' },
              { k:'R_th',    v: `${params.R_th_bc||0.8}°C/W`,                              c:'#f97316' },
              { k:'derate',  v: `${params.T_batt_derate_start||40}~${params.T_batt_derate_end||55}°C`, c:'#f97316' },
            ].map(({ k, v, c }) => (
              <span key={k} style={{ color:COLORS.textMuted }}>
                {k}=<span style={{ color:c, fontWeight:700 }}>{v}</span>
              </span>
            ))}
          </div>

          {/* ── 피드백 배지 ── */}
          <div style={{ marginTop:'6px', display:'flex', gap:'5px', flexWrap:'wrap' }}>
            {[
              { c:C_CELL,  label:e('실시간 측정','Real-time Measure'),  desc:e('V·I·T 연속 수집 → 상태 추정 입력','V·I·T continuous → state estimator input') },
              { c:C_EST,   label:e('상태→제어 피드백','State→Control'),   desc:e('SOC·SoH → 보호 한계 동적 갱신','SOC·SoH → dynamic protection limits') },
              { c:C_ARB,   label:e('중재 피드백','Arb Feedback'),        desc:e('SOC·온도 → 충방전 전력 한계 실시간','SOC·T → power limits in real time') },
              { c:'#ef4444',label:e('결함 즉시 차단','Fault Cutoff'),    desc:e('보호 위반 → 릴레이 차단 (래치 가능)','Protection trip → relay open (latchable)') },
            ].map((fb, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'4px',
                padding:'2px 8px', borderRadius:'4px',
                background:`${fb.c}14`, border:`1px solid ${fb.c}40` }}>
                <span style={{ fontSize:'9px', color:fb.c }}>↺</span>
                <span style={{ fontSize:8.5, fontWeight:600, color:fb.c }}>{fb.label}</span>
                <span style={{ fontSize:7.5, color:COLORS.textMuted }}>{fb.desc}</span>
              </div>
            ))}
          </div>

        </div>
      );
    })()}

    {/* ─── bms_cell: 셀 구성 + 배터리 모델 ─── */}
    {subTab === 'bms_cell' && (()=>{
      const S=params.bms_cell_count_s||96, P=params.bms_cell_count_p||4;
      const Vmax=(params.bms_v_cell_max||4.2)*S, Q_pack=(params.Q_nom||200)*P;
      return (
        <div>
          <ModalSectionTitle icon="🔩" title={e('BMS 셀 구성 & 배터리 모델','BMS Cell Config & Battery Model')} color={COLORS.accent4}/>
          <TogglesRow items={[
            {k:'bms_detailed_enable', label:e('셀 상세 시뮬','Cell Detail'), c:'#a5b4fc'},
            {k:'soh_enable',          label:'SoH 추적',   c:'#34d399', warn:true},
            {k:'sei_enable',          label:'SEI 성장',   c:'#f59e0b', warn:true},
            {k:'li_plating_enable',   label:'Li 플레이팅',c:'#f87171', warn:true},
            {k:'ocv_lut_enable',      label:'OCV LUT',    c:'#60a5fa'},
          ]}/>
          <KpiRow items={[
            {l:'Config',   v:`${S}S${P}P`,               c:COLORS.accent},
            {l:'Cells',    v:S*P,                         c:COLORS.accent2},
            {l:'V (nom)',  v:(3.7*S).toFixed(0), u:'V',  c:COLORS.accent3},
            {l:'V (max)',  v:Vmax.toFixed(0),    u:'V',  c:COLORS.accent4},
            {l:'Q (pack)', v:Q_pack.toFixed(0),  u:'Ah', c:'#a78bfa'},
          ]}/>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px',marginBottom:'14px'}}>
            <PI label={e('직렬 셀 수 S','Series S')} value={S} onChange={v=>up('bms_cell_count_s',v)} unit="ea" step={1} min={50} max={250}/>
            <PI label={e('병렬 셀 수 P','Parallel P')} value={P} onChange={v=>up('bms_cell_count_p',v)} unit="ea" step={1} min={1} max={10}/>
            <PI label="Q_nom (단셀)" value={params.Q_nom||200} onChange={v=>up('Q_nom',v)} unit="Ah" step={5} min={10} max={1000}/>
            <PI label={e('배터리 에너지','Battery Energy')} value={params.battery_energy||75} onChange={v=>up('battery_energy',v)} unit="kWh" step={1} min={10} max={300}/>
          </div>

          {/* 배터리 모델 */}
          <div style={{marginBottom:'12px',padding:'10px 14px',borderRadius:'8px',border:`1px solid ${COLORS.accent4}40`,background:`${COLORS.accent4}06`}}>
            <div style={{fontSize:'10px',fontWeight:700,color:COLORS.accent4,marginBottom:'8px'}}>🔋 {e('배터리 모델','Battery Model')}</div>
            <div style={{display:'flex',gap:'8px',marginBottom:'10px'}}>
              {[{id:'simple',label:'Simple R',desc:e('R_batt 단일 저항','Single R_batt'),c:'#94a3b8'},
                {id:'2RC',  label:'2RC ECM', desc:e('2차 RC 등가회로','2nd-order RC'),  c:COLORS.accent3}].map(m=>(
                <button key={m.id} onClick={()=>up('battery_model',m.id)}
                  style={{flex:1,padding:'6px 8px',borderRadius:'6px',cursor:'pointer',textAlign:'center',
                    border:`2px solid ${params.battery_model===m.id?m.c:COLORS.border}`,
                    background:params.battery_model===m.id?`${m.c}18`:COLORS.bg,
                    color:params.battery_model===m.id?m.c:COLORS.textMuted}}>
                  <div style={{fontWeight:700,fontSize:'11px'}}>{m.label}</div>
                  <div style={{fontSize:8.5,color:COLORS.textMuted,marginTop:'2px'}}>{m.desc}</div>
                </button>
              ))}
            </div>
            {params.battery_model==='2RC'&&(
              <div>
                <div style={{fontSize:'9px',color:COLORS.textMuted,fontFamily:mono,marginBottom:'8px',lineHeight:1.7,
                  padding:'5px 8px',borderRadius:'4px',background:COLORS.bg}}>
                  V_t = OCV − I·R0 − V_RC1 − V_RC2 &nbsp;|&nbsp; τ1=R1·C1, τ2=R2·C2
                </div>
                <div style={{display:'grid',gridTemplateColumns:GRID.auto180,gap:'0 20px'}}>
                  <PI label="R0_base (DC 내부저항)" value={(params.R0_base??params.R_batt??0.08)*1000} onChange={v=>up('R0_base',v/1000)} unit="mΩ" step={1} min={1} max={200} desc="SOC·온도 보정 전 기준값"/>
                  <PI label="R1 (1차 RC 저항)" value={(params.R1_base??0.02)*1000} onChange={v=>up('R1_base',v/1000)} unit="mΩ" step={0.5} min={0.5} max={50} desc={e('단기 분극 (빠른 응답)','Short-term polarization')}/>
                  <PI label="C1 (1차 RC 커패시턴스)" value={params.C1_base??1500} onChange={v=>up('C1_base',v)} unit="F" step={100} min={100} max={20000} desc="τ1=R1×C1 (10~60s 일반)"/>
                  <PI label="R2 (2차 RC 저항)" value={(params.R2_base??0.01)*1000} onChange={v=>up('R2_base',v/1000)} unit="mΩ" step={0.5} min={0.5} max={50} desc={e('장기 분극 (느린 응답)','Long-term polarization')}/>
                  <PI label="C2 (2차 RC 커패시턴스)" value={params.C2_base??10000} onChange={v=>up('C2_base',v)} unit="F" step={500} min={500} max={100000} desc="τ2=R2×C2 (100~600s 일반)"/>
                  <PI label="α_SOC (SOC 의존 보정)" value={params.alpha_SOC??0.5} onChange={v=>up('alpha_SOC',v)} unit="" step={0.05} min={0} max={2} desc="R0_eff=R0·(1+α·ΔSOC)"/>
                  <PI label="k_R_batt (온도 계수)" value={params.k_R_batt??0.03} onChange={v=>up('k_R_batt',v)} unit="/°C" step={0.005} min={0} max={0.1} desc={e('저온 저항 증가율 (~3%/°C)','Cold resistance rate')}/>
                </div>
                <div style={{marginTop:'6px',display:'flex',gap:'12px',fontSize:8.5,color:COLORS.textMuted,fontFamily:mono}}>
                  <span>τ1≈{((params.R1_base??0.02)*(params.C1_base??1500)).toFixed(0)}s</span>
                  <span>τ2≈{((params.R2_base??0.01)*(params.C2_base??10000)).toFixed(0)}s</span>
                  <span>R0@25°C={((params.R0_base??params.R_batt??0.08)*1000).toFixed(1)}mΩ</span>
                </div>
              </div>
            )}
          </div>
          {params.ocv_lut_enable&&(
            <div style={{padding:'10px 14px',borderRadius:'8px',border:'1px solid #60a5fa40',background:'#60a5fa06'}}>
              <div style={{fontSize:'10px',fontWeight:700,color:'#60a5fa',marginBottom:'6px'}}>📈 OCV LUT + Nernst 혼합</div>
              <PI label="LUT 가중치" value={params.ocv_lut_weight??0.70} onChange={v=>up('ocv_lut_weight',v)} unit="" step={0.05} min={0} max={1} desc="1=순수LUT, 0=순수Nernst"/>
              <div style={{marginTop:'4px',fontSize:8.5,color:COLORS.textMuted,fontFamily:mono}}>OCV = w·LUT + (1−w)·Nernst</div>
            </div>
          )}
          {/* [P7-4] ── Ri 손실 KPI 패널 ─────────────────────────────────────
              computeBatteryLosses(I, SOC, T, params) → { P_loss, Ri, V_oc }
              현재 파라미터·온도·SOC 기준 실시간 손실 추정치 시각화              */}
          {(()=>{
            const soc0   = params.soc_init || 0.9;
            const T0     = params.T_amb    || 25;
            const I_pk   = (params.P_max_wheel || 220000) / Math.max(1, params.V_dc || 400);
            const I_nom  = I_pk * 0.3; // 공칭 전류 (피크의 30%)
            const lPk    = computeBatteryLosses(I_pk,  soc0, T0, params);
            const lNom   = computeBatteryLosses(I_nom, soc0, T0, params);
            const lCold  = computeBatteryLosses(I_nom, soc0, -10, params);
            const Ri_mOhm = (lPk.Ri * 1000).toFixed(1);
            const kpiStyle = (color) => ({
              flex:1, minWidth:90, background: `${color}12`,
              border:`1px solid ${color}30`, borderRadius:'7px',
              padding:'8px 10px', textAlign:'center',
            });
            const numStyle = (color) => ({
              fontSize:'17px', fontWeight:800, color, fontFamily:mono,
            });
            const lblStyle = {fontSize:'9px', color:COLORS.textMuted, marginTop:'2px'};
            return (
              <div style={{marginTop:'14px', padding:'10px 14px', borderRadius:'8px',
                border:`1px solid ${COLORS.accent}30`, background:`${COLORS.accent}06`}}>
                <div style={{fontSize:'10px',fontWeight:700,color:COLORS.accent,marginBottom:'10px'}}>
                  🔋 {e('배터리 내부저항 & 손실 (현재 파라미터 기준)','Battery Ri & Loss (current params)')}
                </div>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'8px'}}>
                  <div style={kpiStyle('#a78bfa')}>
                    <div style={numStyle('#a78bfa')}>{Ri_mOhm} mΩ</div>
                    <div style={lblStyle}>Ri @ SOC{Math.round(soc0*100)}% / {T0}°C</div>
                  </div>
                  <div style={kpiStyle('#f59e0b')}>
                    <div style={numStyle('#f59e0b')}>{(lPk.P_loss/1000).toFixed(2)} kW</div>
                    <div style={lblStyle}>{e('피크 Ri 손실','Peak Ri loss')} ({I_pk.toFixed(0)}A)</div>
                  </div>
                  <div style={kpiStyle('#10b981')}>
                    <div style={numStyle('#10b981')}>{(lNom.P_loss/1000).toFixed(2)} kW</div>
                    <div style={lblStyle}>{e('공칭 Ri 손실','Nom Ri loss')} ({I_nom.toFixed(0)}A)</div>
                  </div>
                  <div style={kpiStyle('#ef4444')}>
                    <div style={numStyle('#ef4444')}>{(lCold.Ri*1000).toFixed(1)} mΩ</div>
                    <div style={lblStyle}>Ri @ −10°C {e('(저온 증가)','(cold rise)')}</div>
                  </div>
                  <div style={kpiStyle('#3b82f6')}>
                    <div style={numStyle('#3b82f6')}>{(lPk.V_oc).toFixed(1)} V</div>
                    <div style={lblStyle}>OCV @ SOC{Math.round(soc0*100)}%</div>
                  </div>
                </div>
                <div style={{fontSize:8.5,color:COLORS.textMuted,fontFamily:mono,lineHeight:1.6}}>
                  P_loss = I²·Ri(SOC,T)&nbsp;&nbsp;|&nbsp;&nbsp;
                  Ri 저온(-10°C) 배율: ×{(lCold.Ri/lPk.Ri).toFixed(2)}&nbsp;&nbsp;|&nbsp;&nbsp;
                  V_sag_pk = {(I_pk * lPk.Ri).toFixed(1)}V
                </div>
              </div>
            );
          })()}

          {/* [D4-Ri] ── Ri@T 온도 스위프 차트 (읽기 전용) ─────────────────────
              R_int_T(soc, T, params) → Ω  (battery.js)
              T = −20 ~ 60 °C (5°C 간격) × SOC 3수준(20/50/80%)           */}
          {(()=>{
            const T_arr  = [-20,-15,-10,-5,0,5,10,15,20,25,30,35,40,45,50,55,60];
            const socs   = [0.2, 0.5, 0.8];
            const labels = ['SOC 20%', 'SOC 50%', 'SOC 80%'];
            const colors = ['#ef4444', '#f59e0b', '#10b981'];
            const riData = T_arr.map(T => {
              const row = { T };
              socs.forEach((s, i) => {
                row[labels[i]] = +(R_int_T(s, T, params) * 1000).toFixed(2); // mΩ
              });
              return row;
            });
            const T_amb_now = params.T_amb ?? 25;
            return (
              <div style={{marginTop:'14px', padding:'10px 14px', borderRadius:'8px',
                border:`1px solid ${COLORS.border}`, background:`${COLORS.surface}` }}>
                <div style={{fontSize:'10px', fontWeight:700, color:COLORS.text, marginBottom:'10px'}}>
                  📈 {e('내부저항 Ri vs 온도 스위프 (T = −20 ~ 60 °C)', 'Internal Resistance Ri vs Temperature (−20 ~ 60 °C)')}
                  <span style={{fontWeight:400, color:COLORS.textMuted, marginLeft:'8px', fontSize:'9px'}}>
                    {e('현재 파라미터 기준 · 읽기 전용', 'Based on current params · read-only')}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={riData} margin={{top:4, right:16, left:0, bottom:4}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                    <XAxis dataKey="T" tickFormatter={v => `${v}°`}
                      label={{value:'Temp (°C)', position:'insideBottomRight', offset:-4, fontSize:'9px', fill:COLORS.textMuted}}
                      tick={{fontSize:'9px', fill:COLORS.textMuted}} />
                    <YAxis tickFormatter={v => `${v}`}
                      label={{value:'Ri (mΩ)', angle:-90, position:'insideLeft', offset:8, fontSize:'9px', fill:COLORS.textMuted}}
                      tick={{fontSize:'9px', fill:COLORS.textMuted}} width={42} />
                    <Tooltip
                      formatter={(val, name) => [`${val} mΩ`, name]}
                      labelFormatter={l => `T = ${l}°C`}
                      contentStyle={{fontSize:'10px', background:COLORS.surface, border:`1px solid ${COLORS.border}`}} />
                    <Legend wrapperStyle={{fontSize:'9px'}} />
                    <ReferenceLine x={T_amb_now} stroke="#6366f1" strokeDasharray="4 2"
                      label={{value:`T_amb=${T_amb_now}°C`, fill:'#6366f1', fontSize:'8px', position:'top'}} />
                    {labels.map((lbl, i) => (
                      <Line key={lbl} type="monotone" dataKey={lbl}
                        stroke={colors[i]} strokeWidth={1.8} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <div style={{fontSize:8.5, color:COLORS.textMuted, fontFamily:mono, marginTop:'4px'}}>
                  Ri(SOC,T) = R0_base·f_SOC(SOC)·f_T(T)&nbsp;&nbsp;|&nbsp;&nbsp;
                  R0_base = {((params.R0_base??params.R_batt??0.08)*1000).toFixed(1)}mΩ&nbsp;&nbsp;|&nbsp;&nbsp;
                  {e('저온에서 Ri 급증 → 회생 출력 제한에 직결', 'Ri surge at low-T → directly limits regen power')}
                </div>
              </div>
            );
          })()}
        </div>
      );
    })()}

    {/* ─── bms_soc: SOC 창 + 온도 + 중재 + 열 모델 ─── */}
    {subTab === 'bms_soc' && (
      <div>
        <ModalSectionTitle icon="🌡️" title={e('BMS SOC / 온도 / 중재','BMS SOC / Temp / Arbitration')} color={COLORS.thermalOrange}/>
        <TogglesRow items={[
          {k:'soh_enable',        label:'SoH 추적',   c:'#34d399', warn:true},
          {k:'sei_enable',        label:'SEI 성장',   c:'#f59e0b', warn:true},
          {k:'li_plating_enable', label:'Li 플레이팅',c:'#f87171', warn:true},
        ]}/>

        {/* SOC 창 */}
        <div style={{marginBottom:'10px',padding:'10px 14px',borderRadius:'8px',border:`1px solid ${COLORS.thermalOrange}40`,background:`${COLORS.thermalOrange}06`}}>
          <div style={{fontSize:'10px',fontWeight:700,color:COLORS.thermalOrange,marginBottom:'6px'}}>📊 {e('SOC 보호 윈도우','SOC Protection Window')}</div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('충전 차단 SOC','Charge Cutoff SOC')} value={params.bms_soc_charge_cutoff} onChange={v=>up('bms_soc_charge_cutoff',v)} unit="" step={0.01} min={0.9} max={1.0}/>
            <PI label={e('방전 차단 SOC','Discharge Cutoff SOC')} value={params.bms_soc_discharge_cutoff} onChange={v=>up('bms_soc_discharge_cutoff',v)} unit="" step={0.01} min={0} max={0.15}/>
            <PI label={e('회생 SOC 테이퍼 시작','Regen SOC Taper')} value={params.bms_regen_soc_taper_start} onChange={v=>up('bms_regen_soc_taper_start',v)} unit="" step={0.01} min={0.7} max={0.95} desc={e('이 SOC 이상에서 회생 제동 선형 감소','Regen reduces linearly above this')}/>
          </div>
        </div>

        {/* 온도 */}
        <div style={{marginBottom:'10px',padding:'10px 14px',borderRadius:'8px',border:'1px solid #ef444440',background:'#ef444406'}}>
          <div style={{fontSize:'10px',fontWeight:700,color:'#ef4444',marginBottom:'6px'}}>🌡️ {e('온도 보호 윈도우','Temperature Window')}</div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('충전 최저 온도','Min Charge Temp')} value={params.bms_t_charge_min} onChange={v=>up('bms_t_charge_min',v)} unit="°C" step={1} min={-10} max={10}/>
            <PI label={e('충전 최고 온도','Max Charge Temp')} value={params.bms_t_charge_max} onChange={v=>up('bms_t_charge_max',v)} unit="°C" step={1} min={35} max={55}/>
            <PI label={e('방전 최저 온도','Min Discharge Temp')} value={params.bms_t_discharge_min} onChange={v=>up('bms_t_discharge_min',v)} unit="°C" step={1} min={-40} max={0}/>
            <PI label={e('방전 최고 온도','Max Discharge Temp')} value={params.bms_t_discharge_max} onChange={v=>up('bms_t_discharge_max',v)} unit="°C" step={1} min={50} max={70}/>
            <PI label={e('저온 디레이팅 온도','Cold Derate Temp')} value={params.bms_cold_derate_temp} onChange={v=>up('bms_cold_derate_temp',v)} unit="°C" step={1}/>
            <PI label={e('저온 디레이팅 계수','Cold Derate Factor')} value={params.bms_cold_derate_factor} onChange={v=>up('bms_cold_derate_factor',v)} unit="" step={0.05} min={0.1} max={1}/>
            <PI label={e('고온 충전 차단','Hot Charge Cutoff')} value={params.bms_t_hot_cutoff??55} onChange={v=>up('bms_t_hot_cutoff',v)} unit="°C" step={1} min={45} max={65} desc={e('이 온도 초과 시 충전 완전 차단','Charge fully cut above this')}/>
            <PI label={e('저온 디레이팅 기준 (battery.js)','Batt Cold Derate Ref')} value={params.bms_t_cold_derate??5} onChange={v=>up('bms_t_cold_derate',v)} unit="°C" step={1} min={-10} max={20} desc={e('battery.js 내부 기준 온도','Internal derate ref in battery.js')}/>
          </div>
        </div>

        {/* 배터리 열 모델 */}
        <div style={{marginBottom:'10px',padding:'10px 14px',borderRadius:'8px',border:'1px solid #f9731640',background:'#f9731606'}}>
          <div style={{fontSize:'10px',fontWeight:700,color:'#f97316',marginBottom:'6px'}}>🔥 {e('배터리 열 모델','Battery Thermal Model')}</div>
          <div style={{fontSize:'9px',color:COLORS.textMuted,fontFamily:mono,marginBottom:'8px',lineHeight:1.7,padding:'5px 8px',borderRadius:'4px',background:COLORS.bg}}>
            dT_batt/dt = (Q_gen − Q_cool) / C_th_batt &nbsp;|&nbsp; Q_cool = ΔT / R_th_bc
          </div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('열 용량 C_th_batt','Thermal Capacity')} value={params.C_th_batt??80000} onChange={v=>up('C_th_batt',v)} unit="J/°C" step={5000} min={10000} max={500000} desc={e('클수록 온도 변화 느림','Higher=slower temp change')}/>
            <PI label={e('열저항 R_th_bc','Thermal Resistance')} value={params.R_th_bc??0.8} onChange={v=>up('R_th_bc',v)} unit="°C/W" step={0.05} min={0.05} max={5} desc={e('낮을수록 냉각 효율 높음','Lower=better cooling')}/>
            <PI label={e('디레이팅 시작 온도','Derate Start Temp')} value={params.T_batt_derate_start??40} onChange={v=>up('T_batt_derate_start',v)} unit="°C" step={1} min={30} max={55}/>
            <PI label={e('디레이팅 종료 온도','Derate End Temp')} value={params.T_batt_derate_end??55} onChange={v=>up('T_batt_derate_end',v)} unit="°C" step={1} min={45} max={65}/>
          </div>
        </div>

        {/* 중재 */}
        <div style={{padding:'10px 14px',borderRadius:'8px',border:'1px solid #ef444440',background:'#ef444406'}}>
          <div style={{fontSize:'10px',fontWeight:700,color:'#ef4444',marginBottom:'6px'}}>⚖️ {e('중재 / 전력 제한 (Arbitration)','Arbitration / Power Limits')}</div>
          <div style={{fontSize:'9px',color:COLORS.textMuted,marginBottom:'8px'}}>
            {e('drive_control.js + motor.js에 실시간 반영 — P_regen_limit = min(P_chg_max, arb_chg_limit_kw)',
               'Fed to drive_control.js + motor.js in real-time — P_regen_limit = min(P_chg_max, arb_chg_limit_kw)')}
          </div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('중재 충전 한계 (회생 상한)','Arb Charge Limit')} value={params.arb_chg_limit_kw??70} onChange={v=>up('arb_chg_limit_kw',v)} unit="kW" step={5} min={5} max={300} desc={e('회생 제동 최대 전력 설정','Sets max regen power')}/>
            <PI label={e('중재 방전 한계 (구동 상한)','Arb Discharge Limit')} value={params.arb_dischg_limit_kw??150} onChange={v=>up('arb_dischg_limit_kw',v)} unit="kW" step={10} min={10} max={500}/>
          </div>
        </div>
      </div>
    )}

    {/* ─── bms_volt: 전압 보호 + CC/CV ─── */}
    {subTab === 'bms_volt' && (
      <div>
        <ModalSectionTitle icon="🛡️" title={e('BMS 전압/전류 보호','BMS Voltage / Current Protection')} color={COLORS.accent3}/>
        <div style={{marginBottom:'10px',padding:'7px 12px',borderRadius:'6px',fontSize:'9px',
          background:'#16a34a12',border:'1px solid #16a34a40',
          display:'flex',gap:'12px',flexWrap:'wrap',alignItems:'center'}}>
          <span style={{color:'#4ade80',fontWeight:700}}>🔗 {e('시뮬 연결','Sim Wiring')}</span>
          {[e('V_cell_min→방전차단','V_cell_min→Cutoff'),e('I_dis→토크제한','I_dis→Torque'),e('I_chg→회생제한','I_chg→Regen')]
            .map((s,i)=><span key={i} style={{fontSize:8.5,color:'#4ade80'}}>✓ {s}</span>)}
          <span style={{marginLeft:'auto',fontSize:8.5,color:COLORS.textMuted}}>bms_fault_latch:</span>
          <Tog k="bms_fault_latch" onColor="#ef4444"/>
        </div>

        {(()=>{
          const S=params.bms_cell_count_s||96, Vmax=(params.bms_v_cell_max||4.2)*S;
          return (
            <KpiRow items={[
              {l:'Pack V (max)',  v:Vmax.toFixed(0),                                 u:'V',  c:COLORS.accent3},
              {l:'C-rate 충전',  v:(params.bms_i_charge_max/(params.Q_nom||200)).toFixed(1),u:'C',  c:COLORS.accent2},
              {l:'C-rate 방전',  v:(params.bms_i_discharge_max/(params.Q_nom||200)).toFixed(1),u:'C',  c:COLORS.accent4},
              {l:'Max 충전 전력',v:((params.bms_i_charge_max*Vmax)/1000).toFixed(0), u:'kW', c:COLORS.accent},
              {l:'Max 방전 전력',v:((params.bms_i_discharge_max*Vmax)/1000).toFixed(0),u:'kW', c:'#f97316'},
              {l:'Arb 방전 한계',v:(params.arb_dischg_limit_kw||150).toFixed(0),    u:'kW', c:'#ef4444'},
            ]}/>
          );
        })()}

        <div style={{marginBottom:'10px',padding:'10px 14px',borderRadius:'8px',border:`1px solid ${COLORS.accent3}40`,background:`${COLORS.accent3}06`}}>
          <div style={{fontSize:'10px',fontWeight:700,color:COLORS.accent3,marginBottom:'6px'}}>🔋 {e('셀 전압 보호','Cell Voltage Limits')}</div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('셀 상한 전압 (충전 기준)','Cell Max V')} value={params.bms_v_cell_max} onChange={v=>up('bms_v_cell_max',v)} unit="V" step={0.01} min={3.8} max={4.35}/>
            <PI label={e('셀 하한 전압 (방전 차단)','Cell Min V')} value={params.bms_v_cell_min} onChange={v=>up('bms_v_cell_min',v)} unit="V" step={0.05} min={2.5} max={3.2}/>
            <PI label={e('셀 과전압 보호 (HW)','Cell OVP Threshold')} value={params.bms_cell_v_max??4.25} onChange={v=>up('bms_cell_v_max',v)} unit="V" step={0.01} min={4.0} max={4.5} desc={e('충전 기준보다 높게 (HW 보호)','Higher than charge limit — HW protection')}/>
            <PI label={e('셀 저전압 보호 (HW)','Cell UVP Threshold')} value={params.bms_cell_v_min??2.75} onChange={v=>up('bms_cell_v_min',v)} unit="V" step={0.05} min={2.0} max={3.0} desc={e('방전 차단보다 낮게 (HW 보호)','Lower than discharge cutoff — HW protection')}/>
          </div>
        </div>

        <div style={{padding:'10px 14px',borderRadius:'8px',border:`1px solid ${COLORS.accent3}40`,background:`${COLORS.accent3}06`}}>
          <div style={{fontSize:'10px',fontWeight:700,color:COLORS.accent3,marginBottom:'6px'}}>⚡ {e('충전 프로파일 CC/CV','Charge Profile CC/CV')}</div>
          <div style={{display:'grid',gridTemplateColumns:GRID.auto2,gap:'0 24px'}}>
            <PI label={e('CC→CV 전환 SOC','CC→CV Transition SOC')} value={params.bms_cc_cv_soc??0.80} onChange={v=>up('bms_cc_cv_soc',v)} unit="" step={0.01} min={0.60} max={0.95}/>
            <PI label={e('CV 테이퍼율','CV Taper Rate')} value={params.bms_cv_taper_rate??0.5} onChange={v=>up('bms_cv_taper_rate',v)} unit="C/SOC" step={0.1} min={0.1} max={5.0}/>
            <PI label={e('충전 종료 최소 전류','Min End Current')} value={params.bms_chg_end_current??5} onChange={v=>up('bms_chg_end_current',v)} unit="A" step={1} min={1} max={50}/>
            <PI label={e('급속 충전 최대 SOC','DCFC Max SOC')} value={params.bms_dcfc_max_soc??0.80} onChange={v=>up('bms_dcfc_max_soc',v)} unit="" step={0.01} min={0.60} max={1.00}/>
          </div>
        </div>
      </div>
    )}

    {/* ─── kalman_ext: SOC 칼만 (β-EKF 안내 분리) ─── */}
    {subTab === 'kalman_ext' && (
      <div>
        <ModalSectionTitle icon="📡" title={e('칼만 필터 SOC 추정 — Linear / EKF / UKF','Kalman Filter SOC Estimation')} color="#f59e0b"/>
        <div style={{marginBottom:'8px',padding:'7px 12px',borderRadius:'6px',fontSize:'9px',
          background:params.kalman_enable?'#16a34a12':'#dc262612',
          border:`1px solid ${params.kalman_enable?'#16a34a40':'#dc262640'}`,
          display:'flex',gap:'12px',flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontWeight:700,color:params.kalman_enable?'#4ade80':'#f87171'}}>
            {params.kalman_enable?'✓ '+e('칼만 활성 — SOC 고정밀 추정','Kalman ON — high-accuracy SOC')
                                 :'✗ '+e('칼만 비활성 — 쿨롱 카운팅만','Kalman OFF — Coulomb counting only')}
          </span>
          {params.kalman_enable&&params.battery_model==='2RC'&&<span style={{color:'#4ade80'}}>✓ 2RC ON → V_terminal 고정밀</span>}
          {params.kalman_enable&&params.battery_model!=='2RC'&&<span style={{color:'#facc15'}}>⚠ 2RC OFF → OCV 근사 사용</span>}
        </div>
        <div style={{marginBottom:'10px',padding:'10px',background:'#f59e0b08',borderRadius:'6px',border:'1px solid #f59e0b20',fontSize:9.5,color:COLORS.textDim,fontFamily:mono,lineHeight:1.7}}>
          📐 Linear KF: x̂ = x_pred + K·(z − x_pred), K = P/(P+R)<br/>
          📐 EKF: H = dOCV/dSOC (자코비안), K = P·H·(H·P·H+R)⁻¹<br/>
          📐 UKF: 시그마 포인트 3개 전파 → 분산 추정 (비선형 OCV 최적)
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'12px'}}>
          <label style={{color:COLORS.textDim,fontSize:'11px',fontFamily:mono}}>{e('칼만 활성화','Enable Kalman')}</label>
          <Tog k="kalman_enable" onColor="#f59e0b"/>
        </div>
        <div style={{display:'flex',gap:'6px',marginBottom:'14px'}}>
          {[{id:'linear',label:'Linear KF',desc:e('단순·빠름','Simple·Fast'),c:COLORS.accent2},
            {id:'EKF',   label:'Extended KF',desc:'OCV 자코비안',c:COLORS.accent},
            {id:'UKF',   label:'Unscented KF',desc:e('시그마 포인트 최적','Sigma-point'),c:'#f59e0b'}].map(kt=>(
            <button key={kt.id} onClick={()=>up('kalman_type',kt.id)}
              style={{flex:1,padding:'8px',borderRadius:'8px',cursor:'pointer',textAlign:'center',
                border:`2px solid ${params.kalman_type===kt.id?kt.c:COLORS.border}`,
                background:params.kalman_type===kt.id?`${kt.c}18`:COLORS.bg,
                color:params.kalman_type===kt.id?kt.c:COLORS.textMuted}}>
              <div style={{fontWeight:700,fontSize:'11px'}}>{kt.label}</div>
              <div style={{fontSize:'9px',color:COLORS.textMuted}}>{kt.desc}</div>
            </button>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:GRID.auto180,gap:'0 24px',marginBottom:'12px'}}>
          <PI label={e('Q (프로세스 노이즈)','Q (Process Noise)')} value={params.Q_kf} onChange={v=>up('Q_kf',v)} unit="" step={1e-6} min={1e-7} max={1e-3} desc={e('클수록 측정 의존↑','Larger→trust measurement more')}/>
          <PI label={e('R (측정 노이즈)','R (Measurement Noise)')} value={params.R_kf} onChange={v=>up('R_kf',v)} unit="" step={1e-4} min={1e-5} max={0.1} desc={e('클수록 모델 의존↑','Larger→trust model more')}/>
          <PI label="K_kalman_Q (보정)" value={params.K_kalman_Q} onChange={v=>up('K_kalman_Q',v)} unit="×" step={0.1} min={0.1} max={10}/>
          <PI label="K_kalman_R (보정)" value={params.K_kalman_R} onChange={v=>up('K_kalman_R',v)} unit="×" step={0.1} min={0.1} max={10}/>
        </div>
        {params.kalman_type==='UKF'&&(
          <div style={{marginBottom:'12px',padding:'10px 14px',borderRadius:'8px',border:'1px solid #f59e0b40',background:'#f59e0b06'}}>
            <div style={{fontSize:'10px',fontWeight:700,color:'#f59e0b',marginBottom:'6px'}}>UKF {e('시그마 포인트 파라미터','Sigma-Point Params')}</div>
            <div style={{display:'grid',gridTemplateColumns:GRID.auto180,gap:'0 24px'}}>
              <PI label="α (spread)" value={params.ukf_alpha} onChange={v=>up('ukf_alpha',v)} unit="" step={1e-4} min={1e-4} max={0.1}/>
              <PI label="β (Gaussian=2)" value={params.ukf_beta} onChange={v=>up('ukf_beta',v)} unit="" step={0.5} min={0} max={5}/>
              <PI label="κ (scaling)" value={params.ukf_kappa} onChange={v=>up('ukf_kappa',v)} unit="" step={0.5} min={0} max={3}/>
            </div>
          </div>
        )}
        {/* [B-5] β-EKF 분리 안내 */}
        <div style={{marginTop:'14px',padding:'8px 14px',borderRadius:'7px',
          background:'#818cf808',border:'1px dashed #818cf860',
          display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
          <span style={{fontSize:'9px',color:'#818cf8',fontWeight:700}}>🔮 β Sideslip EKF</span>
          <span style={{fontSize:8.5,color:COLORS.textMuted}}>
            {e('이 탭은 SOC 칼만 전용입니다. β-EKF 파라미터(Q_beta_ekf, R_ay_ekf 등)는 진단/보정 → 타이어/횡력 탭에서 설정하세요.',
               'This tab is SOC Kalman only. β-EKF params (Q_beta_ekf, R_ay_ekf etc.) → Diagnostics → Tyre/Lateral tab.')}
          </span>
          <button onClick={()=>{if(setGroup) setGroup('calib');}}
            style={{padding:'2px 10px',borderRadius:'4px',fontSize:8.5,cursor:'pointer',
              background:'#818cf8',color:'#fff',border:'none',fontWeight:700}}>
            {e('진단/보정 탭 →','Diagnostics →')}
          </button>
        </div>
      </div>
    )}

    {/* ─── bms_output: 밸런싱 + 출력 제어 ─── */}
    {subTab === 'bms_output' && (()=>{
      return (
        <div>
          <ModalSectionTitle icon="🔌" title={e('BMS 출력 제어 — 릴레이·콘택터·밸런싱','BMS Output Control — Relay·Contactor·Balancing')} color="#f97316"/>
          <div style={{marginBottom:'12px',padding:'7px 12px',borderRadius:'6px',fontSize:'9px',
            background:params.bms_detailed_enable?'#818cf812':'#64748b12',
            border:`1px solid ${params.bms_detailed_enable?'#818cf840':'#64748b40'}`,
            display:'flex',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:9.5,fontWeight:700,color:params.bms_detailed_enable?'#a5b4fc':'#94a3b8'}}>
              {params.bms_detailed_enable?e('🟢 셀 상세 시뮬 활성','🟢 Cell Sim ON'):e('🟡 셀 상세 시뮬 비활성','🟡 Cell Sim OFF')}
            </span>
            {!params.bms_detailed_enable&&(
              <button onClick={()=>up('bms_detailed_enable',true)}
                style={{padding:'2px 10px',borderRadius:'4px',fontSize:'9px',cursor:'pointer',
                  background:COLORS.accent4,color:'#000',border:'none',fontWeight:700}}>
                {e('활성화 →','Enable →')}
              </button>
            )}
            <span style={{marginLeft:'auto',fontSize:8.5,color:COLORS.textMuted}}>bms_fault_latch:</span>
            <Tog k="bms_fault_latch" onColor="#ef4444"/>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)',gap:'12px',alignItems:'start'}}>
            <div style={{padding:'10px 14px',borderRadius:'8px',background:COLORS.panel,border:`1px solid ${COLORS.border}`}}>
              <div style={{fontSize:'10px',fontWeight:700,color:'#f59e0b',marginBottom:'8px'}}>⚖️ {e('밸런싱 출력','Balancing Output')}</div>
              <div style={{display:'flex',gap:'6px',marginBottom:'10px'}}>
                {[{k:'passive',label:e('패시브 (열 소산)','Passive'),c:'#f59e0b'},
                  {k:'active', label:e('액티브 (에너지 이동)','Active'), c:'#34d399'}].map(opt=>(
                  <button key={opt.k} onClick={()=>up('bms_balance_strategy',opt.k)}
                    style={{flex:1,padding:'4px 8px',borderRadius:'5px',cursor:'pointer',fontSize:'9px',fontWeight:600,
                      background:(params.bms_balance_strategy||'passive')===opt.k?opt.c:'transparent',
                      color:(params.bms_balance_strategy||'passive')===opt.k?'#000':COLORS.textDim,
                      border:`1px solid ${(params.bms_balance_strategy||'passive')===opt.k?opt.c:COLORS.border}`}}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <PI label={e('밸런싱 임계값 ΔV','Balance Threshold ΔV')} value={params.bms_balance_threshold} onChange={v=>up('bms_balance_threshold',v)} unit="V" step={0.005} min={0.005} max={0.1}/>
              <PI label={e('패시브 저항 R_bal','Passive R_bal')} value={params.bms_R_bal||10} onChange={v=>up('bms_R_bal',v)} unit="Ω" step={1} min={1} max={100}/>
              <PI label={e('액티브 밸런싱 전류','Active Balance I')} value={params.bms_I_bal_active||5} onChange={v=>up('bms_I_bal_active',v)} unit="A" step={0.5} min={0.5} max={20}/>
            </div>
            <div style={{padding:'10px 14px',borderRadius:'8px',background:COLORS.panel,border:`1px solid ${COLORS.border}`}}>
              <div style={{fontSize:'10px',fontWeight:700,color:'#ef4444',marginBottom:'8px'}}>🔌 {e('릴레이 / 콘택터','Relay / Contactor')}</div>
              <PI label={e('프리차지 목표 전압','Precharge Target V')} value={params.bms_precharge_v} onChange={v=>up('bms_precharge_v',v)} unit="V" step={10} desc={e('메인 릴레이 투입 전 목표 전압','Target V before main relay closes')}/>
              <PI label={e('최대 충전 전류','Max Charge I')} value={params.bms_i_charge_max} onChange={v=>up('bms_i_charge_max',v)} unit="A" step={10} min={10} max={1000} desc={e('초과 시 회생 제동 제한','Limits regen braking')}/>
              <PI label={e('최대 방전 전류','Max Discharge I')} value={params.bms_i_discharge_max} onChange={v=>up('bms_i_discharge_max',v)} unit="A" step={10} min={10} max={1500} desc={e('초과 시 구동 토크 제한','Limits drive torque')}/>
              <div style={{marginTop:'8px',padding:'6px 10px',borderRadius:'5px',fontSize:'9px',
                background:'#16a34a12',border:'1px solid #16a34a40',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                <span style={{color:'#4ade80',fontWeight:700}}>🔗 {e('시뮬 연결','Sim Wiring')}</span>
                {[e('V_cell_min→방전차단','V_cell_min→Cutoff'),e('I_dis→토크제한','I_dis→Torque'),e('I_chg→회생제한','I_chg→Regen')]
                  .map((s,i)=><span key={i} style={{fontSize:8.5,color:'#4ade80'}}>✓ {s}</span>)}
              </div>
            </div>
          </div>
        </div>
      );
    })()}

    </div>
  );
}